import { ref, computed } from 'vue'
import { apiClient } from '../utils/apiClient.js'

/**
 * Auth state (shared across app)
 */
const user = ref(null)
const globalUser = ref(null)
const isLoading = ref(false)

// Context selection state
const requiresSelection = ref(false)
const selectionToken = ref(null)
const contexts = ref([])

/**
 * Authentication composable
 * Provides reactive auth state and methods for login/logout
 * Supports unified identity with context selection
 */
export function useAuth() {
  const isAuthenticated = computed(() => !!user.value && apiClient.isAuthenticated())

  /**
   * Fully reset client-side auth state.
   *
   * Called at THREE critical points to prevent cross-tenant data bleed
   * (e.g. Admin A logs out, Admin B of a DIFFERENT business logs in; we
   * must guarantee nothing from A's session survives):
   *   1. Before every login attempt (even if previous state is partial)
   *   2. On logout (full teardown)
   *   3. When /me returns 401 (token invalidated server-side)
   *
   * Clears:
   *   - localStorage auth_token
   *   - All reactive refs (user, globalUser, selection state)
   *   - Dispatches 'auth:reset' so module composables can drop cached data
   *     (cart, last-payment-mode, cached list filters, etc.)
   *
   * Does NOT clear:
   *   - Language preferences (school_locale, billing_locale)
   *   - Device-level preferences (paper size, theme) — these are per-device
   *     not per-user, so they survive login swaps intentionally.
   */
  const resetAuthState = () => {
    apiClient.clearToken()
    user.value = null
    globalUser.value = null
    requiresSelection.value = false
    selectionToken.value = null
    contexts.value = []
    // Broadcast so module-specific composables can flush their caches.
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent('auth:reset'))
    }
  }

  /**
   * Login user
   * Supports both phone (new) and username (legacy) login
   * Returns response which may include requires_selection for multi-context users
   */
  const login = async (phoneOrUsername, password) => {
    // Critical: wipe ANY prior session first. If a stale token exists in
    // localStorage from a different admin/business, the login POST below
    // would use THAT token's Authorization header, potentially confusing
    // the server or leaving residual state if login fails.
    resetAuthState()

    isLoading.value = true

    try {
      const response = await apiClient.post('/v1/auth/login', {
        phone: phoneOrUsername, // New: phone-based login
        username: phoneOrUsername, // Legacy: username fallback
        password,
      })

      // Check if context selection is required
      if (response.requires_selection) {
        requiresSelection.value = true
        selectionToken.value = response.selection_token
        contexts.value = response.contexts || []
        globalUser.value = response.global_user
        return response
      }

      // Direct login - save token and user
      if (response.token) {
        apiClient.saveToken(response.token)
      }
      user.value = response.user
      globalUser.value = response.global_user

      return response
    } catch (error) {
      // Check for license-specific errors - throw with i18n key for translation
      if (error.isLicenseError) {
        const errorCode = error.errorCode
        if (errorCode === 'ALL_LICENSES_EXPIRED') {
          const err = new Error('errors.license.allExpired')
          err.isLicenseError = true
          err.i18nKey = 'errors.license.allExpired'
          throw err
        }
        if (errorCode === 'NO_LICENSE') {
          const err = new Error('errors.license.noLicense')
          err.isLicenseError = true
          err.i18nKey = 'errors.license.noLicense'
          throw err
        }
        if (errorCode === 'LICENSE_EXPIRED') {
          const err = new Error('errors.license.moduleExpired')
          err.isLicenseError = true
          err.i18nKey = 'errors.license.moduleExpired'
          err.i18nParams = { module: error.module || '' }
          throw err
        }
        if (errorCode === 'NO_ACCESS') {
          const err = new Error('errors.license.noAccess')
          err.isLicenseError = true
          err.i18nKey = 'errors.license.noAccess'
          throw err
        }
      }
      throw new Error(error.detail || error.message || 'Login failed')
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Select context (for multi-business users)
   */
  const selectContext = async (profileId) => {
    if (!selectionToken.value) {
      throw new Error('No selection token available')
    }

    isLoading.value = true
    try {
      const response = await apiClient.post('/v1/auth/select-context', {
        selection_token: selectionToken.value,
        profile_id: profileId,
      })

      // Save token and user
      if (response.token) {
        apiClient.saveToken(response.token)
      }
      user.value = response.user
      globalUser.value = response.global_user

      // Clear selection state
      requiresSelection.value = false
      selectionToken.value = null
      contexts.value = []

      return response
    } catch (error) {
      // Check for license-specific errors - throw with i18n key for translation
      if (error.isLicenseError) {
        const errorCode = error.errorCode
        if (errorCode === 'ALL_LICENSES_EXPIRED') {
          const err = new Error('errors.license.allExpired')
          err.isLicenseError = true
          err.i18nKey = 'errors.license.allExpired'
          throw err
        }
        if (errorCode === 'NO_LICENSE') {
          const err = new Error('errors.license.noLicense')
          err.isLicenseError = true
          err.i18nKey = 'errors.license.noLicense'
          throw err
        }
        if (errorCode === 'LICENSE_EXPIRED') {
          const err = new Error('errors.license.moduleExpired')
          err.isLicenseError = true
          err.i18nKey = 'errors.license.moduleExpired'
          err.i18nParams = { module: error.module || '' }
          throw err
        }
      }
      throw new Error(error.detail || error.message || 'Context selection failed')
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Cancel context selection and return to login
   */
  const cancelSelection = () => {
    requiresSelection.value = false
    selectionToken.value = null
    contexts.value = []
    globalUser.value = null
  }

  /**
   * Logout user — fully tears down client-side session state.
   */
  const logout = async () => {
    try {
      await apiClient.post('/v1/auth/logout')
    } catch (error) {
      // Logout API failure is non-critical — the server-side session
      // will expire on its own, and we still fully clear local state.
    } finally {
      resetAuthState()
    }
  }

  /**
   * Fetch current user info. On 401 we treat the session as dead and
   * perform a full auth reset so no stale data lingers in reactive state.
   */
  const fetchMe = async () => {
    if (!apiClient.isAuthenticated()) {
      return null
    }

    isLoading.value = true
    try {
      const response = await apiClient.get('/v1/auth/me')
      user.value = response.user
      return response.user
    } catch (error) {
      // Token rejected server-side or user deactivated — full reset.
      resetAuthState()
      throw error
    } finally {
      isLoading.value = false
    }
  }

  /**
   * Check if user has access to a module
   */
  const hasModuleAccess = (moduleName) => {
    if (!user.value) return false
    return user.value.allowed_modules?.includes(moduleName) || false
  }

  /**
   * Check if user has a specific role
   */
  const hasRole = (role) => {
    if (!user.value) return false
    return user.value.role === role
  }

  /**
   * Role permissions map - mirrors backend auth/permissions.go
   */
  const rolePermissions = {
    admin: [
      'dashboard:view',
      'loans:view', 'loans:create', 'loans:close',
      'collections:view', 'collections:record',
      'customers:view', 'customers:create',
      'collectors:manage',
      'settings:view', 'settings:edit',
      'reports:view',
    ],
    staff: [
      'dashboard:view',
      'loans:view', 'loans:create',
      'collections:view', 'collections:record',
      'customers:view', 'customers:create',
      'reports:view',
    ],
    collector: [
      'collections:view',
      'collections:record',
      'loans:view', // Can view loan details when clicking from collections
    ],
  }

  /**
   * Check if user has a specific permission based on their role
   * @param {string} permission - Permission string like 'dashboard:view'
   * @returns {boolean}
   */
  const hasPermission = (permission) => {
    if (!user.value) return false
    const role = user.value.role
    const perms = rolePermissions[role] || []
    return perms.includes(permission)
  }

  /**
   * Get all permissions for current user's role
   * @returns {string[]}
   */
  const getPermissions = () => {
    if (!user.value) return []
    return rolePermissions[user.value.role] || []
  }

  return {
    // State
    user,
    globalUser,
    isLoading,
    isAuthenticated,

    // Context selection state
    requiresSelection,
    contexts,

    // Methods
    login,
    selectContext,
    cancelSelection,
    logout,
    fetchMe,
    resetAuthState,
    hasModuleAccess,
    hasRole,
    hasPermission,
    getPermissions,
  }
}
